import { useId, useState, type ReactNode } from 'react';
import { googleMapsRouteUrl, type GeoPoint } from '@app/shared/geo';
import { useMe } from '../auth/useMe';
import { canManageSettings } from '../settings/access';
import { SettingsDialog } from '../settings/SettingsDialog';
import { WarehouseSettings } from '../settings/WarehouseSettings';
import { useCompanySettings } from '../settings/company';
import { RowMenu } from '../ui/RowMenu';
import { MapDialog } from './MapDialog';
import { MapPreview } from './MapPreview';
import { NumberInput } from '../ui/NumberInput';
import { useGeoConfig } from '../geo/config';
import { distanceText, parseRouteKm, pickedPlacePatch, retypedPlacePatch, placeCopyText, placeMapsUrl, placeNavigateUrl, routeKmField, tollsText, travelText, type Distance, type Place, type PlacePatch } from './place';
import { PlaceSearch } from './PlaceSearch';
import { useDefaultRoute } from './defaultRoute';
import { RouteDialog } from './RouteDialog';
import './fields.css';

import { AddressSearch } from './AddressSearch';

export { AddressSearch };

/**
 * The saved distance of the event (edit form): shown while the point is the saved one; with the automatic Google
 * route also its travel time and tolls for the saved "Unikaj płatnych".
 */
export type SavedDistance = {
  point: GeoPoint | null;
  distanceKm: number | null;
  distanceSource: Distance['distanceSource'];
  travelMinutes?: number | null | undefined;
  tollsGrosze?: number | null | undefined;
  avoidTolls?: boolean | undefined;
  /** The automatic distance (the Google route) whatever was chosen or typed (owner 2026-10-23); null without one. */
  autoDistance?: { km: number; source: 'google' } | null | undefined;
};

/**
 * The distance from the warehouse and where it comes from (owner: "a jak liczysz odległość?"). The last decision wins
 * (spec 2026-10-23-trasa-km-transport): "km wg trasy" typed by hand, else the route chosen in "Pokaż trasę", else
 * the saved Google route of this point; without any of them no km (owner 2026-10-23: no straight line). Where the form
 * edits "km wg trasy", the field itself shows those km and their source (owner 2026-10-23); typing replaces them and
 * "przywróć z trasy" brings them back. With Google routes: the travel time and tolls, "Pokaż trasę", "Unikaj
 * płatnych". A gear opens the warehouse settings.
 */
export function DistanceNote({ place, saved, onChange, startsAt }: { place: Place; saved?: SavedDistance | undefined; onChange?: ((patch: PlacePatch) => void) | undefined; startsAt?: string | null | undefined }) {
  const { me } = useMe();
  const settings = useCompanySettings().data;
  const { config } = useGeoConfig();
  const [open, setOpen] = useState(false);
  const [routes, setRoutes] = useState(false);
  const routeId = useId();
  const canManage = !!me && canManageSettings(me).warehouse;
  const point = place.locationPoint;
  const routeKm = place.routeKm;
  const samePoint = !!point && !!saved?.point && saved.point.lat === point.lat && saved.point.lng === point.lng;
  const sameTolls = (place.avoidTolls ?? false) === (saved?.avoidTolls ?? false);
  const chosen = place.chosenRoute ?? null;
  // owner 2026-10-23 13:34: a new point (or "Unikaj płatnych") — its default Google route at once, as a preview
  const asking = !!onChange && !(samePoint && sameTolls) && !chosen && config.routes && !!settings?.warehousePoint;
  const preview = useDefaultRoute(point, place.avoidTolls ?? false, startsAt ?? null, asking);
  const previewed = asking ? (preview.data ?? null) : null;
  if (!point) return null;
  const typed = routeKm !== undefined ? parseRouteKm(routeKm) : ({ ok: true, value: null } as const);
  const auto =
    samePoint && sameTolls
      ? (saved?.autoDistance ?? (saved?.distanceSource === 'google' && saved.distanceKm !== null ? { km: saved.distanceKm, source: 'google' as const } : null))
      : previewed?.km !== undefined
        ? { km: previewed.km, source: 'google' as const }
        : null;
  const used: Distance =
    typed.ok && typed.value !== null
      ? { routeKm: typed.value, distanceKm: Math.round(typed.value), distanceSource: 'route' }
      : routeKm === undefined && saved?.distanceSource === 'route'
        ? // typed by someone else; this role does not see "km wg trasy" (it stays whatever the point)
          { routeKm: null, distanceKm: saved.distanceKm, distanceSource: 'route' }
        : chosen
          ? { routeKm: null, distanceKm: Math.round(chosen.km), distanceSource: 'chosen', chosenLabel: chosen.label }
          : auto
            ? { routeKm: null, distanceKm: auto.km, distanceSource: 'google' }
            : // owner 2026-10-23: no straight line — no km until Google, a chosen route or typed km
              { routeKm: null, distanceKm: null, distanceSource: null };
  const field = routeKm !== undefined ? routeKmField({ typed: routeKm, chosen, auto, warehouse: !!settings?.warehousePoint, pending: !(samePoint && sameTolls), routesOn: config.routes, checking: asking && preview.checking }) : null;
  // the travel time and tolls: of the chosen route, else of the saved automatic Google route of this very place
  const autoRoute = samePoint && sameTolls && saved?.distanceSource !== 'chosen';
  const minutes = chosen ? chosen.minutes : autoRoute ? (saved?.travelMinutes ?? null) : (previewed?.minutes ?? null);
  const tolls = chosen ? (chosen.tollsGrosze ?? null) : autoRoute ? (saved?.tollsGrosze ?? null) : (previewed?.tollsGrosze ?? null);
  const extra = [travelText(minutes) && `dojazd ${travelText(minutes)}`, tollsText(tolls)].filter(Boolean).join(' · ');
  // the field shows the km and their source: above it only the travel time and tolls (uncluttered)
  const text = field && onChange ? '' : distanceText(used);
  const routeUrl = googleMapsRouteUrl(settings?.warehousePoint, point);
  const showRoutes = config.routes && !!settings?.warehousePoint;
  const mayChoose = !!onChange && place.chosenRoute !== undefined;
  return (
    <div className="distance" data-feature-id="events.settings">
      <span className="distance-note">
        <span>
          {text || (settings?.warehousePoint ? '' : 'Odległość od magazynu pokaże się po ustawieniu adresu magazynu.')}
          {extra && <span className="distance-extra">{text ? ` · ${extra}` : extra}</span>}
        </span>
        {canManage && <RowMenu icon="gear" label="Ustawienia: magazyn i odległość" featureId="events.settings" actions={[{ label: 'Adres magazynu…', onSelect: () => setOpen(true) }]} />}
      </span>
      {showRoutes && (
        <span className="distance-route-actions" data-feature-id="events.route">
          <button className="btn small" type="button" onClick={() => setRoutes(true)}>
            Pokaż trasę
          </button>
          {chosen && mayChoose && (
            <button className="btn small ghost" type="button" onClick={() => onChange!({ chosenRoute: null })}>
              Wróć do trasy automatycznej
            </button>
          )}
          {onChange && place.avoidTolls !== undefined && (
            <label className="check">
              <input type="checkbox" checked={place.avoidTolls} onChange={(e) => onChange({ avoidTolls: e.target.checked, ...(chosen && mayChoose ? { chosenRoute: null } : {}) })} />
              <span>Unikaj płatnych</span>
            </label>
          )}
        </span>
      )}
      {field && onChange && (
        <span className="distance-route">
          <label htmlFor={routeId}>km wg trasy</label>
          <NumberInput
            id={routeId}
            kind="decimal"
            className="distance-route-input"
            value={field.value}
            onValueChange={(text) => onChange({ routeKm: text })}
            // review M5: the route's km are a hint, not a value — only typed km are sent
            placeholder={field.suggested || 'np. 412,5'}
            aria-describedby={`${routeId}-hint`}
          />
          {field.note && <span className="distance-route-source">{field.note}</span>}
          {field.typed && field.restore && (
            <button className="btn small ghost" type="button" onClick={() => onChange({ routeKm: '' })} title={`Wróć do km: ${field.restore}`}>
              przywróć z trasy
            </button>
          )}
          {routeUrl && (
            <a className="btn small ghost" href={routeUrl} target="_blank" rel="noreferrer">
              Otwórz trasę w Google Maps
            </a>
          )}
        </span>
      )}
      {field && onChange && (
        <span id={`${routeId}-hint`} className={typed.ok ? 'hint' : 'field-error'}>
          {typed.ok
            ? field.typed && chosen
              ? 'Wpisane km liczą się zamiast wybranej trasy; czas dojazdu i opłaty zostają z trasy.'
              : 'Te km liczą transport w kosztorysie. Wpisz własne, gdy pojedziesz inaczej.'
            : 'Wpisz liczbę km, np. 412,5 (najwyżej jedno miejsce po przecinku).'}
        </span>
      )}
      {open && me && (
        <SettingsDialog title="Magazyn" onClose={() => setOpen(false)}>
          <WarehouseSettings me={me} heading={false} />
        </SettingsDialog>
      )}
      {routes && (
        <RouteDialog
          to={point}
          startsAt={startsAt ?? null}
          avoidTolls={place.avoidTolls ?? false}
          chosen={chosen}
          canChoose={mayChoose}
          onUse={(route, avoidTolls) =>
            // the chosen route replaces km typed before (the last decision wins; the server does the same)
            onChange?.({ chosenRoute: route, ...(routeKm !== undefined && routeKm.trim() !== '' ? { routeKm: '' } : {}), ...(place.avoidTolls !== undefined && avoidTolls !== place.avoidTolls ? { avoidTolls } : {}) })
          }
          onClose={() => setRoutes(false)}
        />
      )}
    </div>
  );
}

/**
 * "Otwórz w Google Maps", "Nawiguj" (owner 2026-10-21 22:54: to the place only, no waypoints — the crew taps it on the
 * phone) and "Kopiuj lokalizację" (owner remark 3/12: easy to send the place to the crew). `navigate`: false when the
 * role may not see the address (spec 2026-10-22-mapy-google §6).
 */
export function PlaceActions({ place, name, navigate = true, before }: { place: Place; name: string; navigate?: boolean; before?: ReactNode }) {
  const [copied, setCopied] = useState('');
  const url = placeMapsUrl(place);
  const go = navigate ? placeNavigateUrl(place) : null;
  const text = placeCopyText({ ...place, name });
  if (!url && !text && !before) return null;
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied('Skopiowano lokalizację.');
    } catch {
      setCopied('Nie udało się skopiować — zaznacz adres i skopiuj go ręcznie.');
    }
  }
  return (
    <span className="place-actions">
      {before}
      {go && (
        <a className="btn small" href={go} target="_blank" rel="noreferrer" data-feature-id="events.route">
          Nawiguj
        </a>
      )}
      {url && (
        <a className="btn small" href={url} target="_blank" rel="noreferrer">
          Otwórz w Google Maps
        </a>
      )}
      {text && (
        <button className="btn small" type="button" onClick={() => void copy()}>
          Kopiuj lokalizację
        </button>
      )}
      {copied && (
        <span className="hint" role="status">
          {copied}
        </span>
      )}
    </span>
  );
}

/**
 * The place of an event (owner remark 3/12): the name with suggestions while typing — the company's places, then
 * Google's (spec 2026-10-22-mapy-google; a pasted Google Maps link too) — the address (found on OpenStreetMap
 * without Google), "Wybierz na mapie" — a big map in a dialog (owner, 2026-10-19) — a small map as a preview once
 * there is a point, "Lokalizacja nieprecyzyjna" — on until someone puts the pin by hand or unticks it — and the
 * distance with "Pokaż trasę".
 */
export function PlaceField({
  value,
  onChange,
  name,
  namePlaceholder,
  saved,
  startsAt,
  hint,
  bare = false,
  filled = false,
}: {
  value: Place;
  onChange: (patch: PlacePatch) => void;
  name: string;
  namePlaceholder?: string;
  /** The saved distance (edit form), to say when it is the Google route. */
  saved?: SavedDistance | undefined;
  /** The start of the event (the traffic at the departure of "Pokaż trasę"). */
  startsAt?: string | null | undefined;
  /** Under the name: where a filled place came from (owner 2026-10-30: „Miejsce z reguły „PFK” — zmień w Ustawieniach”). */
  hint?: string | null | undefined;
  /** A place that is no event's (the default place, a rule of „Skojarzenia miejsc”): no distance, route or links. */
  bare?: boolean;
  /** The place is still the filled one (owner 2026-10-30): a pin put on the map brings its own name, not the filled one. */
  filled?: boolean;
}) {
  const [choosing, setChoosing] = useState(false);
  const addressId = useId();
  const { config } = useGeoConfig();
  const google = config.search === 'google';
  const mapButton = (
    <button className="btn small" type="button" onClick={() => setChoosing(true)}>
      Wybierz na mapie
    </button>
  );
  // a new address found by the search is another place: its ids and notes go, and so does a route chosen for it
  const otherPlace: PlacePatch = { ...(value.googlePlaceId !== undefined ? { googlePlaceId: '' } : {}), ...(value.venueId !== undefined ? { venueId: null } : {}), placeNotes: '', ...(value.chosenRoute ? { chosenRoute: null } : {}) };
  return (
    <fieldset className="place-field" data-feature-id="events.edit">
      <legend className="visually-hidden">Miejsce</legend>
      <PlaceSearch
        label="Miejsce"
        value={value.location}
        onChange={(location) => onChange(retypedPlacePatch(value, { location }))}
        onPick={(picked) => onChange(pickedPlacePatch(value, picked))}
        selectOnFocus={filled}
        placeholder={namePlaceholder ?? (google ? 'np. Lublin teatr — albo wklej link z Map Google' : 'np. PGE Narodowy')}
      />
      {hint && (
        <p className="hint field-hint place-auto" data-feature-id="events.place_defaults">
          {hint}
        </p>
      )}
      {value.placeNotes && (
        <p className="hint place-notes" data-feature-id="events.venues">
          Notatki o miejscu: {value.placeNotes}
        </p>
      )}
      {google ? (
        <div className="field">
          <label htmlFor={addressId}>Adres</label>
          <div className="address-search-box">
            <input id={addressId} value={value.locationAddress} autoComplete="off" placeholder="Uzupełnia się po wyborze miejsca" onChange={(e) => onChange(retypedPlacePatch(value, { locationAddress: e.target.value }))} />
            {mapButton}
          </div>
        </div>
      ) : (
        <AddressSearch
          label="Adres"
          value={value.locationAddress}
          placeholder="Wpisz adres albo nazwę obiektu, np. Ołowianka 1, Gdańsk"
          onChange={(locationAddress) => onChange(retypedPlacePatch(value, { locationAddress }))}
          onPick={(found) => onChange({ locationAddress: found.label, locationPoint: { lat: found.lat, lng: found.lng }, ...otherPlace })}
          action={mapButton}
        />
      )}
      {value.locationPoint && <MapPreview label="Mapa miejsca" point={value.locationPoint} />}
      {choosing && <MapDialog value={filled ? { ...value, location: '' } : value} onUse={(patch) => onChange(filled && patch.location === undefined ? { ...patch, location: '' } : patch)} onClose={() => setChoosing(false)} />}
      <div className="place-meta">
        <label className="check">
          <input type="checkbox" checked={value.locationApprox} onChange={(e) => onChange({ locationApprox: e.target.checked })} />
          <span>Lokalizacja nieprecyzyjna</span>
        </label>
      </div>
      <p className="hint field-hint">
        {value.locationApprox
          ? 'Zostaw zaznaczone, dopóki pinezka nie stoi dokładnie przy wjeździe lub scenie. Wskaż miejsce przyciskiem „Wybierz na mapie” albo odznacz, gdy miejsce jest pewne.'
          : 'Miejsce wskazane dokładnie.'}
      </p>
      {!bare && <DistanceNote place={value} saved={saved} onChange={onChange} startsAt={startsAt} />}
      {!bare && <PlaceActions place={value} name={name} />}
    </fieldset>
  );
}
